import React, { useEffect, useState } from "react";
import {
  Alert,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from "react-native";
import * as Sentry from "@sentry/react-native";
import Purchases, {
  type CustomerInfo,
  type PurchasesEntitlementInfo,
  type PurchasesPackage,
} from "react-native-purchases";
import Feather from "@expo/vector-icons/Feather";
import { Ionicons, MaterialCommunityIcons } from "@expo/vector-icons";

import ScreenLayout from "@/Components/Layouts/Native/ScreenLayout";
import { Spacings } from "@/styling/spacings";
import { themes } from "@/Components/Styling/themeConfigs";
import PremiumFeatureBaseCard from "@/screens/Explore/Components/UnlockFlow/StageThree/Components/PremiumFeatureBaseCard";

function SubscriptionScreen(): React.JSX.Element {
  const [isLoading, setIsLoading] = useState(true);
  const [plans, setPlans] = useState<PurchasesPackage[]>([]);
  const [selectedPlanId, setSelectedPlanId] = useState<string>("");
  const [activeEntitlement, setActiveEntitlement] =
    useState<PurchasesEntitlementInfo | null>(null);

  useEffect(() => {
    async function loadSubscriptionData(): Promise<void> {
      try {
        setIsLoading(true);
        // Check if the user has an active subscription.
        // This is tricky because subscription means non-cancelled subscription.
        // So we check for entitlement meaning if they have access to premium features..
        const customerInfo = await Purchases.getCustomerInfo();
        checkAndSetForActiveSubscription(customerInfo);

        // Fetch the current offerings
        const offeringsData = await Purchases.getOfferings();
        const possiblePlans = offeringsData.current?.availablePackages;
        setPlans(possiblePlans ?? []);

        if (possiblePlans && possiblePlans.length > 0 && possiblePlans[0]) {
          setSelectedPlanId(possiblePlans[0].identifier);
        }
      } catch (error) {
        Sentry.captureException(error);
        Alert.alert(
          "Error",
          "Something went wrong. Please try again or reach out to support@getkaiapp.com",
        );
      } finally {
        setIsLoading(false);
      }
    }

    loadSubscriptionData();
  }, []);

  const checkAndSetForActiveSubscription = (
    customerInfo: CustomerInfo,
  ): boolean => {
    const entitlements = customerInfo.entitlements.active;
    setActiveEntitlement(Object.values(entitlements)[0] || null);
    return Object.keys(entitlements).length > 0;
  };

  const handlePurchase = async (): Promise<void> => {
    const selectedPackage = plans.find(
      (plan) => plan.identifier === selectedPlanId,
    );
    if (!selectedPackage) {
      Alert.alert("Error", "Selected package not found");
      return;
    }

    try {
      setIsLoading(true);

      const { customerInfo } = await Purchases.purchasePackage(selectedPackage);
      const hasNewSubscription = checkAndSetForActiveSubscription(customerInfo);

      if (hasNewSubscription) {
        Alert.alert(
          "Success",
          "Thank you for your subscription! You now have access to premium features.",
        );
      }
    } catch (error) {
      // Don't show error if user just cancelled
      if (
        error instanceof Error &&
        "userCancelled" in error &&
        !error.userCancelled
      ) {
        Sentry.captureException(error);
        Alert.alert(
          "Purchase Failed",
          "message" in error
            ? error.message
            : "There was a problem with your purchase. Please try again.",
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  const currentTheme = themes.morning;
  const styles = StyleSheet.create({
    content: {
      flex: 1,
      padding: 16,
    },
    premiumOptionsContainer: {
      width: "100%",
      justifyContent: "space-between",
    },
    premiumOption: {
      width: "100%",
      marginBottom: Spacings.s2,
      alignItems: "center",
      paddingVertical: Spacings.s1,
      marginHorizontal: 4,
      backgroundColor: "#f5f5f5",
      borderRadius: 10,
      borderWidth: 0.5,
      borderColor: "#3b5998",
    },
    premiumOptionSelected: {
      borderWidth: 1.5,
      borderColor: "#3b5998",
    },
    sectionHeader: {
      fontSize: currentTheme.typography.h2.size.mobile,
      fontWeight: "bold",
      marginTop: Spacings.s2,
      marginBottom: Spacings.s1,
    },
    button: {
      borderRadius: 10,
      alignItems: "center",
      justifyContent: "center",
      marginVertical: Spacings.s2,
      paddingHorizontal: Spacings.s3,
      height: 50,
      backgroundColor: "#3b5998",
      borderWidth: 1,
      borderColor: "black",
    },
    buttonText: {
      color: "#eee",
      fontSize: currentTheme.typography.h2.size.mobile,
      fontWeight: "bold",
    },
    activePlanContainer: {
      backgroundColor: "#f5f5f5",
      borderRadius: 10,
      padding: Spacings.s3,
      marginBottom: Spacings.s3,
      alignItems: "center",
    },
    activePlanTitle: {
      fontSize: 20,
      fontWeight: "bold",
      marginBottom: Spacings.s1,
    },
    activePlanText: {
      fontSize: 16,
      textAlign: "center",
      marginBottom: Spacings.s1,
    },
  });

  return (
    <ScreenLayout isLoading={isLoading}>
      <ScrollView style={styles.content}>
        {activeEntitlement ? (
          <View style={styles.activePlanContainer}>
            <Feather name="check-circle" size={48} color="#3b5998" />
            <Text style={styles.activePlanTitle}>Premium Active</Text>
            <Text style={styles.activePlanText}>
              You have full access to all premium features.
            </Text>
          </View>
        ) : (
          <></>
        )}
        <>
          <Text style={styles.sectionHeader}>{"Available Plans:"}</Text>

          <View style={styles.premiumOptionsContainer}>
            {plans.map((plan) => (
              <Pressable
                key={plan.identifier}
                style={[
                  styles.premiumOption,
                  selectedPlanId === plan.identifier &&
                    styles.premiumOptionSelected,
                ]}
                onPress={() => setSelectedPlanId(plan.identifier)}
              >
                <Text
                  style={{
                    fontWeight:
                      selectedPlanId === plan.identifier ? "bold" : "normal",
                  }}
                >
                  {plan.product.title}
                </Text>
                <Text>{plan.product.priceString}</Text>
              </Pressable>
            ))}
          </View>
          <Text style={styles.sectionHeader}>{"What's included:"}</Text>

          <PremiumFeatureBaseCard
            iconComponent={<Feather name="users" size={24} color="black" />}
            title={"Additional Users"}
            description={"See additional users near you"}
          />
          <PremiumFeatureBaseCard
            iconComponent={
              <MaterialCommunityIcons
                name="advertisements-off"
                size={24}
                color="black"
              />
            }
            title={"Ad-Free Guarantee"}
            description={"Enjoy peace of mind—no ads now or in the future"}
          />

          <Text style={styles.sectionHeader}>
            {"Renewal & Cancellation Policy:"}
          </Text>

          <PremiumFeatureBaseCard
            iconComponent={
              <Ionicons name="refresh-circle-outline" size={24} color="black" />
            }
            title={"Auto Renewal"}
            description={`Subscription automatically renews unless auto-renewal is turned off at least 24 hours before the end of the current period.`}
          />

          <PremiumFeatureBaseCard
            iconComponent={
              <MaterialCommunityIcons name="cancel" size={24} color="black" />
            }
            title={"Cancellation Policy"}
            description={`You can manage and cancel your subscription by visiting your ${Platform.OS === "ios" ? "Apple App Store" : "Google Play Store"} account settings after purchase.`}
          />

          <Pressable
            disabled={!selectedPlanId}
            style={styles.button}
            onPress={() => handlePurchase()}
          >
            <Text style={styles.buttonText}>Subscribe Now</Text>
          </Pressable>
        </>
        {/* Always show restore purchases option */}
        <Pressable
          style={{
            alignItems: "center",
            marginTop: 10,
            marginBottom: 20,
          }}
          onPress={async () => {
            try {
              setIsLoading(true);
              const customerInfo = await Purchases.restorePurchases();
              const restored = checkAndSetForActiveSubscription(customerInfo);

              if (restored) {
                Alert.alert("Success", "Your subscription has been restored!");
              } else {
                Alert.alert(
                  "No Subscriptions",
                  "No active subscriptions were found to restore.",
                );
              }
            } catch (error) {
              Sentry.captureException(error);
              Alert.alert(
                "Error",
                "Failed to restore purchases. Please try again.",
              );
            } finally {
              setIsLoading(false);
            }
          }}
        >
          <Text style={{ color: "#3b5998" }}>Restore Purchases</Text>
        </Pressable>
      </ScrollView>
    </ScreenLayout>
  );
}

export default SubscriptionScreen;
